import React, { Component } from 'react';
import Tree from 'react-ui-tree';
import Axios from 'axios';
import { defineMessages, FormattedMessage, injectIntl, useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import intlShape from '@/shared/utils/intlShape';
import handleHttpError from '@/shared/services/HandleHttpError';
import ZendeskHelp from '@/shared/components/ZendeskHelp';
import SimplePanel from '@/shared/components/SimplePanel';
import api from '../../../shared/api';
import config from '../../../config';
import {
  AddIcon,
  DeleteIcon,
  DocumentIcon,
  FolderIcon,
  SaveIcon,
  UndoIcon,
} from '../../../shared/components/Icons';
import LoadingAnimation from '../../../shared/components/LoadingAnimation';
import EditableTextbox from '../../../shared/components/editable/EditableTextbox';
import FixedTree from '../../../shared/components/FixedTree';
import {
  CanCreate,
  CanDelete,
  CanUpdate,
  showWhenPermission,
} from '../../../shared/components/security';
import { getDocumentTypes } from './documentTypesService';
import Button from '@/shared/components/Button';

const messages = defineMessages({
  EnterTag: 'Enter tag',
  DocumentFolderStructure: 'Document folder structure',
  DocumentTypesForX: 'Document types for {x}',
  SelectDocumentTypeToAdd: 'Select type to add',
  ManageDocumentTypesForFolder: 'Manage document types for folder',
  RestructureDocumentTree: 'Restructure tree',
  RestructureDocumentTreeInfo:
    "Use drag and drop to restructure the tree above. \r\nDon't forget to click the save button afterwards.",
  Save: 'Save',
  Cancel: 'Cancel',
  AddDocumentTypesToParentNodes: 'Add document types to parent nodes',
});
const deleteCollapsedStateRecursive = ({ children, ...currentNode }) => {
  const copy = {
    ...currentNode,
    children: children.map(deleteCollapsedStateRecursive),
  };
  delete copy.collapsed;

  return copy;
};
class DocumentTreeConfiguration extends Component {
  state = { tree: null, tags: null, newTag: '' };

  mapTreeNode = ({ children = null, ...node }) => {
    const result = {
      ...node,
      collapsed: false,
      children: children && children.length ? children.map(this.mapTreeNode) : [],
    };
    return result;
  };

  componentDidMount() {
    const { intl } = this.props;
    Promise.all([
      api.base.get(`${config.appUrls.documents}/api/Tree`),
      api.base.get(`${config.appUrls.documents}/api/Tag`),
      getDocumentTypes(intl),
    ]).then(([{ data: treeData }, { data: tags }, documentTypes]) => {
      this.setState({
        tree: { children: treeData.map(this.mapTreeNode), collapsed: false },
        tags,
        documentTypes,
      });
    });
  }

  findOrCreateTag = tagName =>
    new Promise((resolve, reject) => {
      const { tags } = this.state;
      const tag = tags.find(t => t.name.toLowerCase() === tagName.toLowerCase());
      if (tag) {
        resolve(tag);
      } else {
        api.base.post(`${config.appUrls.documents}/api/Tag`, { name: tagName }).then(({ data }) => {
          this.setState(
            state => ({
              tags: [...state.tags, data],
            }),
            () => resolve(data),
          );
        }, reject);
      }
    });

  addTag = (node, tagName) => {
    tagName = tagName.replace(';', '');
    if (!tagName) return;
    this.findOrCreateTag(tagName).then(tag => {
      this.addTagToNode(node, tag);
    });
  };
  addTagToNode = (node, tag) => {
    if (!tag || !tag.id) return;
    const { tree, restructure } = this.state;
    if (restructure) return;
    const updatedTree = this.updateNodeRecursive(
      node.id,
      {
        children: [
          ...node.children,
          {
            name: tag.name,
            tagId: tag.id,
            children: [],
            collapsed: false,
          },
        ],
      },
      tree,
    );

    this.handleChange(updatedTree);
  };

  updateNodeRecursive = (id, updatedProperties, { children = [], ...currentNode }) => {
    const copy = {
      ...currentNode,
      children: children.map(c => this.updateNodeRecursive(id, updatedProperties, c)),
    };
    if (currentNode.id && currentNode.id === id) {
      return { ...copy, ...updatedProperties };
    }
    return copy;
  };
  deleteNode = node => {
    const { tree, restructure } = this.state;
    if (restructure) return;
    const updatedTree = this.deleteNodeRecursive(node.id, tree);

    this.handleChange(updatedTree);
  };
  deleteNodeRecursive = (id, { children, ...currentNode }) => ({
    ...currentNode,
    children: children.filter(c => !c.id || c.id !== id).map(c => this.deleteNodeRecursive(id, c)),
  });

  addTagToRoot = tagName => {
    this.findOrCreateTag(tagName).then(tag => {
      const { tree, restructure } = this.state;
      if (restructure) return;
      const updatedTree = {
        ...tree,
        children: [
          ...tree.children,
          {
            name: tag.name,
            tagId: tag.id,
            children: [],
            collapsed: false,
          },
        ],
      };

      this.handleChange(updatedTree);
    });
  };

  getDocumentTypes = node => {
    const { intl } = this.props;
    this.setState(
      {
        documentTypeInfo: {
          tagIds: node.tagIds,
          tagId: node.tagId,
          tagNames: node.treeTagNames,
          loading: true,
          types: null,
          otherTypes: null,
        },
      },
      () => {
        Promise.all([
          api.base.post(`${config.appUrls.documents}/api/TagType/Types`, { tagIds: node.tagIds }),
          node.tagIds.length > 1
            ? api.base.get(`${config.appUrls.documents}/api/TagType/Types/${node.tagId}`)
            : Promise.resolve({ data: [] }),
        ]).then(
          ([{ data: types }, { data: tagTypes }]) => {
            this.setState(({ documentTypeInfo }) => ({
              documentTypeInfo: {
                ...documentTypeInfo,
                types,
                otherTypes: tagTypes.filter(x => types.indexOf(x) < 0),
                loading: false,
              },
            }));
          },
          error => {
            handleHttpError(intl)(error);
            this.setState({
              documentTypeInfo: null,
            });
          },
        );
      },
    );
  };

  handleAddDocumentType = e => this.addDocumentType(e.target.value);

  addDocumentType = type => {
    const {
      documentTypeInfo: { tagIds },
    } = this.state;
    if (!type || !tagIds) return;

    return api.base
      .post(`${config.appUrls.documents}/api/TagType/AddType/${type}`, {
        tagIds,
      })
      .then(
        () => {
          this.setState(({ documentTypeInfo }) => ({
            documentTypeInfo: {
              ...documentTypeInfo,
              types: [...documentTypeInfo.types, parseInt(type)],
              otherTypes: documentTypeInfo.otherTypes.filter(x => x !== type),
            },
          }));
        },
        error => {
          const { intl } = this.props;
          handleHttpError(intl)(error);
        },
      );
  };
  removeDocumentType = type => {
    const {
      documentTypeInfo: { tagId },
    } = this.state;
    if (type === null || type === undefined || !tagId) return;

    return api.base
      .delete(`${config.appUrls.documents}/api/TagType/DeleteType/${type}/${tagId}`)
      .then(
        () => {
          this.setState(({ documentTypeInfo }) => ({
            documentTypeInfo: {
              ...documentTypeInfo,
              types: documentTypeInfo.types.filter(x => x !== type),
            },
          }));
        },
        error => {
          const { intl } = this.props;
          handleHttpError(intl)(error);
        },
      );
  };

  renderNode = node => {
    const { intl } = this.props;
    const { restructure } = this.state;
    return (
      <div className="node">
        <span className="node-name">{node.name}</span>
        {!restructure && (
          <span className="hide-on-drag node-actions">
            <button
              type="button"
              className="btn btn-xs  btn-default margin5R"
              title={intl.formatMessage(messages.ManageDocumentTypesForFolder)}
              onMouseDown={e => e.stopPropagation()}
              onClick={e => {
                this.getDocumentTypes(node);
                e.stopPropagation();
              }}
            >
              <DocumentIcon />
            </button>
            <CanCreate on="settings">
              <span className="margin5R" onMouseDown={e => e.stopPropagation()}>
                <EditableTextbox
                  value=""
                  placeholder={intl.formatMessage(messages.EnterTag)}
                  list="tags"
                  onChange={tag => this.addTag(node, tag)}
                  displayValue={
                    <button type="button" className="btn btn-xs btn-default">
                      <AddIcon />
                    </button>
                  }
                  inputClassName="tag-input input-sm"
                />
              </span>
            </CanCreate>
            <CanDelete on="settings">
              <button
                type="button"
                className="btn btn-xs btn-default"
                style={{ visibility: node.children.length ? 'hidden' : 'visible' }}
                onMouseDown={e => e.stopPropagation()}
                onClick={e => {
                  this.deleteNode(node);
                  e.stopPropagation();
                }}
              >
                <DeleteIcon />
              </button>
            </CanDelete>
          </span>
        )}
      </div>
    );
  };
  handleChange = tree => {
    if (typeof this.updateToken !== 'undefined') {
      this.updateToken.cancel('Operation canceled due to new request.');
    }
    this.updateToken = Axios.CancelToken.source();
    return api.base
      .put(`${config.appUrls.documents}/api/Tree`, tree.children, {
        cancelToken: this.updateToken.token,
      })
      .then(
        ({ data }) => {
          this.setState({
            tree: { children: data.map(this.mapTreeNode), collapsed: false },
            documentTypeInfo: null,
          });
        },
        error => {
          if (!Axios.isCancel(error)) {
            const { intl } = this.props;
            return handleHttpError(intl)(error);
          }
        },
      )
      .finally(() => {
        this.setState({ saving: false });
      });
  };
  startRestructure = () => {
    const { tree } = this.state;
    // Store copy of original tree state
    const treeCopy = deleteCollapsedStateRecursive(tree);

    this.setState({ restructure: true, treeCopy, documentTypeInfo: null });
  };
  saveRestructure = () => {
    const { treeCopy, tree, activeTeam, saving } = this.state;
    if (saving) return;
    const ignoreCollapsedTree = deleteCollapsedStateRecursive(tree);

    if (JSON.stringify(treeCopy) === JSON.stringify(ignoreCollapsedTree)) {
      this.setState({ restructure: false, treeCopy: null });

      return;
    }
    this.setState({ saving: true });
    return this.handleChange(tree).then(() => {
      this.setState({
        restructure: false,
        saving: false,
      });
      const { intl } = this.props;
      ToastrService.changesSaved(intl);
      return true;
    });
  };

  cancelRestructure = () => {
    const { treeCopy, saving } = this.state;
    if (saving) return;

    this.setState({ restructure: false, tree: treeCopy, treeCopy: null });
  };

  render() {
    const { tree, tags, newTag, documentTypeInfo, documentTypes, restructure, saving } = this.state;
    const { intl } = this.props;

    const availableTypes =
      documentTypeInfo && documentTypeInfo.types && documentTypeInfo.types.length > 0
        ? documentTypes.filter(x => documentTypeInfo.types.indexOf(x.key) === -1)
        : documentTypes;
    const selectedTypes =
      documentTypeInfo && documentTypeInfo.types && documentTypeInfo.types.length > 0
        ? documentTypes.filter(x => documentTypeInfo.types.indexOf(x.key) >= 0)
        : [];

    const tagDocumentTypes =
      documentTypeInfo?.otherTypes && documentTypeInfo.otherTypes.length > 0
        ? documentTypes.filter(x => documentTypeInfo.otherTypes.indexOf(x.key) >= 0)
        : [];

    return (
      <div>
        {tree ? (
          <SimplePanel
            title={intl.formatMessage(messages.DocumentFolderStructure)}
            article="5434863900050"
          >
            <div className="flex-container folder-wrapper">
              <div className="flex-child-block">
                <div className="document-tree hidden-root margin10T inline-block">
                  <CanCreate on="settings">
                    <div className="input-group">
                      <input
                        type="text"
                        maxLength="200"
                        className="form-control tag-input"
                        value={newTag}
                        placeholder={intl.formatMessage(messages.EnterTag)}
                        onChange={e => this.setState({ newTag: e.target.value.replace(';', '') })}
                        list="tags"
                        autoComplete="off"
                        onKeyDown={e => {
                          if (e.keyCode === 13) {
                            this.addTagToRoot(newTag);
                            this.setState({ newTag: '' });
                          } else if (e.keyCode === 27) {
                            this.setState({ newTag: '' });
                          }
                        }}
                      />
                      <span className="input-group-btn">
                        <button
                          type="button"
                          className="btn btn-default"
                          onClick={() => {
                            this.addTagToRoot(newTag);
                            this.setState({ newTag: '' });
                          }}
                        >
                          <AddIcon />
                        </button>
                      </span>
                    </div>
                  </CanCreate>
                  <CanUpdate
                    on="settings"
                    fallback={
                      <FixedTree tree={tree} paddingLeft={20} renderNode={this.renderNode} />
                    }
                  >
                    {restructure && !saving ? (
                      <Tree paddingLeft={20} tree={tree} renderNode={this.renderNode} />
                    ) : (
                      <FixedTree tree={tree} paddingLeft={20} renderNode={this.renderNode} />
                    )}
                    <div className="margin10T">
                      {!restructure ? (
                        <button
                          type="button"
                          className="btn btn-default"
                          onClick={() => this.startRestructure()}
                        >
                          <FolderIcon />
                          <FormattedMessage {...messages.RestructureDocumentTree} />
                        </button>
                      ) : (
                        <div>
                          <div className="alert alert-info linebreak">
                            <FormattedMessage {...messages.RestructureDocumentTreeInfo} />
                          </div>
                          <Button
                            showProgress
                            btnClassName="btn-inverse margin10R"
                            onClick={() => this.saveRestructure()}
                          >
                            <SaveIcon />
                            &nbsp;
                            <FormattedMessage {...messages.Save} />
                          </Button>
                          <Button onClick={() => this.cancelRestructure()} disabled={saving}>
                            <UndoIcon />
                            &nbsp;
                            <FormattedMessage {...messages.Cancel} />
                          </Button>
                        </div>
                      )}
                    </div>
                  </CanUpdate>
                </div>
              </div>
              <div className="flex-child-block folder-details">
                {documentTypeInfo && (
                  <SimplePanel
                    title={intl.formatMessage(messages.DocumentTypesForX, {
                      x: documentTypeInfo.tagNames,
                    })}
                    className="margin10T"
                  >
                    {!documentTypeInfo.loading && (
                      <div className="margin10T">
                        <CanCreate on="settings">
                          <div className="margin10B">
                            <select
                              className="form-control"
                              value=""
                              onChange={this.handleAddDocumentType}
                            >
                              <option value="">
                                {intl.formatMessage(messages.SelectDocumentTypeToAdd)}
                              </option>
                              {availableTypes.map(x => (
                                <option key={x.key} value={x.key}>
                                  {x.label}
                                </option>
                              ))}
                            </select>
                          </div>
                        </CanCreate>
                        <div>
                          {selectedTypes.map(x => (
                            <div key={x.key} className="pad5">
                              <span className="margin5R">{x.label}</span>
                              <CanUpdate on="Settings">
                                <DeleteIcon
                                  onClick={() => this.removeDocumentType(x.key)}
                                  className="pull-right"
                                />
                              </CanUpdate>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </SimplePanel>
                )}
                {tagDocumentTypes.length > 0 && !documentTypeInfo.loading ? (
                  <CanCreate on="settings">
                    <SimplePanel
                      title={intl.formatMessage(messages.AddDocumentTypesToParentNodes)}
                      className="margin10T"
                    >
                      <div className="margin10T">
                        <div>
                          {tagDocumentTypes.map(x => (
                            <div key={x.key} className="pad5">
                              <span className="margin5R">{x.label}</span>
                              <AddIcon
                                onClick={() => this.addDocumentType(x.key)}
                                className="pull-right"
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    </SimplePanel>
                  </CanCreate>
                ) : null}
              </div>
            </div>
          </SimplePanel>
        ) : (
          <LoadingAnimation />
        )}
        <datalist id="tags">{tags && tags.map(t => <option key={t.id} value={t.name} />)}</datalist>
      </div>
    );
  }
}

DocumentTreeConfiguration.propTypes = {
  intl: intlShape.isRequired,
};

export default showWhenPermission('settings', 'read')(injectIntl(DocumentTreeConfiguration));
